import { useRollbar } from '@rollbar/react'
import { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { Comment } from 'modules/comments/components/comment'
import { CommentSortSelector } from 'modules/comments/components/comment-sort-selector'
import { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { CommentsContextType } from 'modules/comments/components/comments-list/CommentsList.types'
import { CommentListWithLoader } from 'modules/comments/components/comments-list/comment-list-with-loader'
import { MessageBox } from 'modules/comments/components/message-box/message-box'
import { StickyMessageBox } from 'modules/comments/components/message-box/stycky-message-box'
import { StickyMessageBoxWithReply } from 'modules/comments/components/message-box/stycky-message-box-with-reply'
import {
  CommentHighlightProvider,
  useCommentHighlight,
} from 'modules/comments/contexts/comment-highlight'
import { useCommentsWithCacheManagement } from 'modules/comments/hooks/use-comments-with-cache-management'
import { CommentsTypeEnum, OnErrorType } from 'modules/comments/types'
import { saveCommentSortPreference } from 'modules/comments/utils/comment-preferences'
import { CommentsHighlightContext } from 'modules/community/components/post/types'
import { PinnedCommentsList } from './pinned-comments-list'

export const CommentsContext = createContext({} as CommentsContextType)
export const useCommentsContext = () => useContext(CommentsContext)

interface BaseCommentListProps {
  commentsType: CommentsTypeEnum
  shouldFetch?: boolean
  readOnly?: boolean
  onError?: OnErrorType
  targetCommentId?: number
  commentsHighlightContext?: CommentsHighlightContext
}
type CommentsCourseAndCommunityProps =
  | {
      commentsType: CommentsTypeEnum.Community
      community: {
        communityPath: string
        postId: number
      }
    }
  | {
      commentsType: CommentsTypeEnum.Page
      course: {
        pageId: number
      }
    }
export type CommentsProps = BaseCommentListProps & CommentsCourseAndCommunityProps

const CommentsListInner = (props: CommentsProps) => {
  const {
    commentsType,
    shouldFetch = true,
    readOnly = false,
    onError,
    targetCommentId,
    commentsHighlightContext,
  }: CommentsProps = props

  const rollbar = useRollbar()
  const { t } = useTranslation()
  const { setHighlightedComment, highlightedComment } = useCommentHighlight()

  const isCommunityProps = 'community' in props
  const resourceId = isCommunityProps ? props.community.postId : props.course.pageId

  const [activeComment, setActiveComment] = useState<CommentInterface | null>(null)
  const [pinnedComments, setPinnedComments] = useState<CommentInterface[]>([])

  const {
    comments,
    replies,
    sortOption,
    updateReplies,
    handleSortChange: originalHandleSortChange,
    handleLoadMoreGap,
    getPageCommentsQuery,
    setComments,
  } = useCommentsWithCacheManagement({
    commentsType,
    pageId: resourceId,
    shouldFetch,
    onError: error => {
      onError && onError(error)
      rollbar.error('Comments error', error as Error)
    },
    targetCommentId,
    isCommunityComments: isCommunityProps,
    setHighlightedComment,
    highlightedComment,
  })

  const handleSortChange = (newSortOption: typeof sortOption) => {
    saveCommentSortPreference(newSortOption, rollbar.warning)
    originalHandleSortChange(newSortOption)
  }

  const isReplyMessageBox = activeComment !== null
  const isAllowCommentingAndNotReplyMessageBox = !isReplyMessageBox && !readOnly

  useEffect(() => {
    if (!activeComment) return
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setActiveComment(null)
      }
    }
    document.addEventListener('keydown', handleEscKey)
    return () => document.removeEventListener('keydown', handleEscKey)
  }, [activeComment])

  useEffect(() => {
    if (commentsHighlightContext?.targetCommentId && !!comments?.length) {
      const commentElement = document.getElementById(
        `comment-${commentsHighlightContext?.targetCommentId}`,
      )
      const container = document.querySelector('[data-scrollable="comments-container"]')
      commentElement && container?.scrollTo({ top: commentElement.offsetTop, behavior: 'smooth' })
    }
  }, [commentsHighlightContext?.targetCommentId, !!comments?.length])

  const replyInfo =
    !!activeComment && isCommunityProps
      ? {
          userId: activeComment.userId,
          displayName: activeComment.userName,
          profileImage: activeComment.userAvatarUrl,
        }
      : undefined

  const extraProviderValue = isCommunityProps
    ? {
        community: {
          communityPath: props.community.communityPath,
          postId: props.community.postId,
        },
      }
    : {
        course: {
          pageId: props.course.pageId,
        },
      }

  const activeCommentParentId =
    (!Array.isArray(activeComment?.parent) ? activeComment?.parent.id : activeComment?.id) || null

  return (
    <CommentsContext.Provider
      value={{
        commentsType,
        commentsList: comments,
        setComments,
        repliesList: replies,
        updateReplies,
        pinnedComments,
        setPinnedComments,
        readOnly,
        extra: extraProviderValue,
        currentSortOption: sortOption,
      }}
    >
      <div className="relative">
        <div className="flex flex-col items-stretch space-y-5 md:space-y-6">
          {(comments.length > 0 || pinnedComments.length > 0) && (
            <CommentSortSelector
              currentSort={sortOption}
              onSortChange={handleSortChange}
              disabled={getPageCommentsQuery.isLoading}
            />
          )}

          {!isCommunityProps && isAllowCommentingAndNotReplyMessageBox && (
            <MessageBox parentId={null} onError={onError} />
          )}

          {isCommunityProps && (
            <PinnedCommentsList
              postId={props.community.postId}
              setActiveComment={setActiveComment}
              activeComment={activeComment}
              onError={onError}
              targetCommentId={targetCommentId}
            />
          )}

          <CommentListWithLoader
            items={comments}
            renderItem={comment => {
              const highlighted = commentsHighlightContext?.searchHighlightedComments?.find(
                h => h.id === comment.id,
              )
              return (
                <Comment
                  key={comment.id}
                  data={{ ...(highlighted ?? {}), ...comment }}
                  level={0}
                  setActiveComment={setActiveComment}
                  activeComment={activeComment}
                  onError={onError}
                  replies={replies[comment.id]?.replies}
                  loadMoreReplies={handleLoadMoreGap}
                />
              )
            }}
            onLoadMore={handleLoadMoreGap}
            parentId={null}
            loadMoreLabel={t('comments.components.comments.load_more_label')}
          />
        </div>

        {/* NOTE: It's a workaround to have the comment field re-created due to React life-cycle. Since Slate is uncontrollable and we can only use initValue on mount component */}
        {!isCommunityProps ? null : isAllowCommentingAndNotReplyMessageBox ? (
          <StickyMessageBox parentId={null} onError={onError} />
        ) : (
          <StickyMessageBoxWithReply
            parentId={activeCommentParentId}
            setActiveComment={setActiveComment}
            onError={onError}
            replyInfo={replyInfo}
            setResponses={(parentId, newReply) =>
              updateReplies({
                commentId: parentId,
                newReplies: isCommunityProps
                  ? [...(replies[parentId]?.replies ?? []), newReply]
                  : [newReply, ...(replies[parentId]?.replies ?? [])],
              })
            }
          />
        )}
      </div>
    </CommentsContext.Provider>
  )
}

export const CommentsList = (props: CommentsProps) => {
  return (
    <CommentHighlightProvider>
      <CommentsListInner {...props} />
    </CommentHighlightProvider>
  )
}
